/**
 * Default editor shortcuts.
 * Used where App.KeyboardShortcuts is not available.
 *
 * Note: TinyMCE uses 'meta' as a cross-platform modifier (ctrl on PC, command on macOS).
 *
 * @type {Object}
 */
const defaultShortcuts = {
    bold: 'meta+b',
    italic: 'meta+i',
    underline: 'meta+u',
    link: 'meta+k',
    strikethrough: 'meta+shift+s',
    codesample: 'meta+shift+x',
    blockquote: 'meta+shift+.',
    numlist: 'meta+.',
    bullist: 'meta+/',
    outdent: 'meta+[',
    indent: 'meta+]',
    removeformat: 'meta+\\',
    superscript: 'meta+h',
    subscript: 'meta+l',
    paragraph: 'meta+alt+0',
    heading1: 'meta+alt+1',
    heading2: 'meta+alt+2',
    heading3: 'meta+alt+3',
    heading4: 'meta+alt+4',
    heading5: 'meta+alt+5',
    heading6: 'meta+alt+6',
    cannedresponses: 'ctrl+shift+1',
    selfservice: 'ctrl+shift+2',
    submit: 'ctrl+enter',
};

/**
 * Get editor shortcuts.
 * Returns shortcuts from the keyboard shortcuts system if available (operator panel),
 * otherwise returns defaults (frontend).
 *
 * @returns {Object}
 */
const getShortcuts = function () {
    return typeof App !== 'undefined' && App.KeyboardShortcuts && typeof App.KeyboardShortcuts.getEditorShortcuts === 'function'
        ? App.KeyboardShortcuts.getEditorShortcuts()
        : defaultShortcuts;
};

/**
 * Check if keyboard shortcuts are globally enabled.
 *
 * @returns {boolean}
 */
const isEnabled = function () {
    return typeof App === 'undefined'
        || typeof App.KeyboardShortcuts === 'undefined'
        || typeof App.KeyboardShortcuts.isEnabled !== 'function'
        || App.KeyboardShortcuts.isEnabled();
};

/**
 * Convert shortcut string to TinyMCE format and keycode.
 *
 * @param {string|null} shortcut - e.g., 'meta+shift+s'
 * @returns {{pattern: string, keyCode: number, key: string}|null}
 */
const parseShortcut = function (shortcut) {
    if (!shortcut) return null;

    let parts = shortcut.toLowerCase().split('+');
    let key = parts.pop();

    // Build TinyMCE pattern (e.g., 'meta+shift+83')
    let pattern = parts.join('+');

    // Map key to keycode
    let keyCodeMap = {
        'a': 65, 'b': 66, 'c': 67, 'd': 68, 'e': 69, 'f': 70, 'g': 71, 'h': 72,
        'i': 73, 'j': 74, 'k': 75, 'l': 76, 'm': 77, 'n': 78, 'o': 79, 'p': 80,
        'q': 81, 'r': 82, 's': 83, 't': 84, 'u': 85, 'v': 86, 'w': 87, 'x': 88,
        'y': 89, 'z': 90,
        '0': 48, '1': 49, '2': 50, '3': 51, '4': 52, '5': 53, '6': 54, '7': 55, '8': 56, '9': 57,
        'enter': 13, 'space': 32, 'tab': 9, 'esc': 27, 'backspace': 8,
        '.': 190, '/': 191, '[': 219, ']': 221, '\\': 220, '`': 192,
        '-': 189, '=': 187, ';': 186, "'": 222, ',': 188,
    };

    let keyCode = keyCodeMap[key];
    if (!keyCode) return null;

    if (pattern) {
        pattern += '+' + keyCode;
    } else {
        pattern = String(keyCode);
    }

    return {pattern: pattern, keyCode: keyCode, key: key};
};

/**
 * Register all editor shortcuts.
 *
 * @param {Object} editor - TinyMCE editor instance
 */
const registerShortcuts = function (editor) {
    let shortcuts = getShortcuts();
    let enabled = isEnabled();

    // If shortcuts are globally disabled, remove all default shortcuts and don't register any.
    if (!enabled) {
        editor.on('init', function () {
            Object.keys(editor.shortcuts.shortcuts).forEach(function (key) {
                delete editor.shortcuts.shortcuts[key];
            });
        });
        return;
    }

    // Register shortcuts after TinyMCE has initialized and registered its defaults.
    editor.on('init', function () {
        // Bold
        let boldShortcut = parseShortcut(shortcuts['bold']);
        if (boldShortcut) {
            editor.addShortcut(boldShortcut.pattern, 'Bold.', function () {
                editor.execCommand('Bold');
            });
        }

        // Italic
        let italicShortcut = parseShortcut(shortcuts['italic']);
        if (italicShortcut) {
            editor.addShortcut(italicShortcut.pattern, 'Italic.', function () {
                editor.execCommand('Italic');
            });
        }

        // Underline
        let underlineShortcut = parseShortcut(shortcuts['underline']);
        if (underlineShortcut) {
            editor.addShortcut(underlineShortcut.pattern, 'Underline.', function () {
                editor.execCommand('Underline');
            });
        }

        // Link
        let linkShortcut = parseShortcut(shortcuts['link']);
        if (linkShortcut) {
            editor.addShortcut(linkShortcut.pattern, 'Insert link.', function () {
                editor.execCommand('mceLink');
            });
        }

        // Superscript
        let superscriptShortcut = parseShortcut(shortcuts['superscript']);
        if (superscriptShortcut) {
            editor.addShortcut(superscriptShortcut.pattern, 'Insert superscript.', function () {
                editor.execCommand('Superscript');
            });
        }

        // Subscript
        let subscriptShortcut = parseShortcut(shortcuts['subscript']);
        if (subscriptShortcut) {
            editor.addShortcut(subscriptShortcut.pattern, 'Insert subscript.', function () {
                editor.execCommand('Subscript');
            });
        }

        // Strikethrough
        let strikethroughShortcut = parseShortcut(shortcuts['strikethrough']);
        if (strikethroughShortcut) {
            editor.addShortcut(strikethroughShortcut.pattern, 'Strikethrough the text.', function () {
                editor.execCommand('Strikethrough');
            });
        }

        // Code sample
        let codesampleShortcut = parseShortcut(shortcuts['codesample']);
        if (codesampleShortcut) {
            editor.addShortcut(codesampleShortcut.pattern, 'Insert code fragment.', function () {
                editor.execCommand('CodeSample');
            });
        }

        // Block quote
        let blockquoteShortcut = parseShortcut(shortcuts['blockquote']);
        if (blockquoteShortcut) {
            editor.addShortcut(blockquoteShortcut.pattern, 'Insert block quote.', function () {
                editor.execCommand('mceBlockQuote');
            });
        }

        // Numbered list
        let numlistShortcut = parseShortcut(shortcuts['numlist']);
        if (numlistShortcut) {
            editor.addShortcut(numlistShortcut.pattern, 'Insert ordered list.', function () {
                editor.execCommand('InsertOrderedList');
            });
        }

        // Bullet list
        let bullistShortcut = parseShortcut(shortcuts['bullist']);
        if (bullistShortcut) {
            editor.addShortcut(bullistShortcut.pattern, 'Insert unordered list.', function () {
                editor.execCommand('InsertUnorderedList');
            });
        }

        // Outdent
        let outdentShortcut = parseShortcut(shortcuts['outdent']);
        if (outdentShortcut) {
            editor.addShortcut(outdentShortcut.pattern, 'Outdent the current selection.', function () {
                editor.execCommand('Outdent');
            });
        }

        // Indent
        let indentShortcut = parseShortcut(shortcuts['indent']);
        if (indentShortcut) {
            editor.addShortcut(indentShortcut.pattern, 'Indent the current selection.', function () {
                editor.execCommand('Indent');
            });
        }

        // Remove format
        let removeformatShortcut = parseShortcut(shortcuts['removeformat']);
        if (removeformatShortcut) {
            editor.addShortcut(removeformatShortcut.pattern, 'Remove formatting from the current selection.', function () {
                editor.execCommand('RemoveFormat');
            });
        }

        // Paragraph
        let paragraphShortcut = parseShortcut(shortcuts['paragraph']);
        if (paragraphShortcut) {
            editor.addShortcut(paragraphShortcut.pattern, 'Insert a paragraph.', function () {
                editor.execCommand('FormatBlock', false, 'p');
            });
        }

        // Heading 1
        let heading1Shortcut = parseShortcut(shortcuts['heading1']);
        if (heading1Shortcut) {
            editor.addShortcut(heading1Shortcut.pattern, 'Insert header 1.', function () {
                editor.execCommand('FormatBlock', false, 'h1');
            });
        }

        // Heading 2
        let heading2Shortcut = parseShortcut(shortcuts['heading2']);
        if (heading2Shortcut) {
            editor.addShortcut(heading2Shortcut.pattern, 'Insert header 2.', function () {
                editor.execCommand('FormatBlock', false, 'h2');
            });
        }

        // Heading 3
        let heading3Shortcut = parseShortcut(shortcuts['heading3']);
        if (heading3Shortcut) {
            editor.addShortcut(heading3Shortcut.pattern, 'Insert header 3.', function () {
                editor.execCommand('FormatBlock', false, 'h3');
            });
        }

        // Heading 4
        let heading4Shortcut = parseShortcut(shortcuts['heading4']);
        if (heading4Shortcut) {
            editor.addShortcut(heading4Shortcut.pattern, 'Insert header 4.', function () {
                editor.execCommand('FormatBlock', false, 'h4');
            });
        }

        // Heading 5
        let heading5Shortcut = parseShortcut(shortcuts['heading5']);
        if (heading5Shortcut) {
            editor.addShortcut(heading5Shortcut.pattern, 'Insert header 5.', function () {
                editor.execCommand('FormatBlock', false, 'h5');
            });
        }

        // Heading 6
        let heading6Shortcut = parseShortcut(shortcuts['heading6']);
        if (heading6Shortcut) {
            editor.addShortcut(heading6Shortcut.pattern, 'Insert header 6.', function () {
                editor.execCommand('FormatBlock', false, 'h6');
            });
        }

        // Canned responses
        let cannedresponsesShortcut = parseShortcut(shortcuts['cannedresponses']);
        if (cannedresponsesShortcut) {
            editor.addShortcut(cannedresponsesShortcut.pattern, 'Insert a canned response.', function () {
                editor.execCommand('mceCannedResponses');
            });
        }

        // Self-service
        let selfserviceShortcut = parseShortcut(shortcuts['selfservice']);
        if (selfserviceShortcut) {
            editor.addShortcut(selfserviceShortcut.pattern, 'Insert a self-service article.', function () {
                editor.execCommand('mceSelfService');
            });
        }

        // Submit form
        let submitShortcut = parseShortcut(shortcuts['submit']);
        if (submitShortcut) {
            editor.addShortcut(submitShortcut.pattern, 'Submit form.', function () {
                if (editor.getElement().form instanceof HTMLFormElement) {
                    $(editor.getElement().form).trigger('submit');
                }
            });
        }

        // Remove disabled/changed shortcuts.
        $.each(defaultShortcuts, function(btnName, defaultShortcut) {
            let currentShortcut = shortcuts[btnName];

            // Shortcut is disabled - remove the default TinyMCE shortcut binding.
            if (!currentShortcut) {
                editor.shortcuts.remove(parseShortcut(defaultShortcut).pattern);
                return;
            }

            // Remove the default shortcut if it has been changed.
            if (currentShortcut !== defaultShortcut) {
                editor.shortcuts.remove(parseShortcut(defaultShortcut).pattern);
            }
        });
    });

    // Update tooltips - must be done in PreInit as buttons are registered by then.
    editor.on('PreInit', function () {
        $.each(defaultShortcuts, function(btnName) {
            let currentShortcut = shortcuts[btnName];

            // Don't update tooltip if shortcut is disabled.
            if (!currentShortcut) {
                return;
            }

            // Update tooltip if button exists.
            if (editor.ui.registry.getAll().buttons.hasOwnProperty(btnName)) {
                // translations seem to occur sometime after PreInit...
                let tooltip = editor.ui.registry.getAll().buttons[btnName].tooltip;
                editor.ui.registry.getAll().buttons[btnName].tooltip
                    = tinymce.util.I18n.translate(tooltip) + " (" + currentShortcut + ")";
            }
        });
    });
};

export {registerShortcuts, defaultShortcuts};

